import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  useTheme,
  Stack,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import VariablePills from '../../components/VariablePills';

interface Automation {
  _id: string;
  name: string;
  description?: string;
  enabled: boolean;
  conditions: {
    type: 'email_not_opened' | 'email_not_clicked' | 'email_not_replied' | 'email_not_delivered';
    delayDays: number;
    filters: {
      emailSent?: boolean;
      excludeDeleted?: boolean;
      excludeSpam?: boolean;
      excludeBounced?: boolean;
    };
  };
  action: {
    type: string;
    templateId: string;
    template?: {
      _id: string;
      name: string;
      subject: string;
      body: string;
      templateType: string;
    };
    maxExecutions: number;
  };
  stats: {
    totalExecutions: number;
    successfulExecutions: number;
    failedExecutions: number;
    lastExecutedAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Template {
  _id: string;
  name: string;
  templateType: string;
  subject: string;
  body: string;
}

const CRMAutomations: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState<Automation | null>(null);
  
  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [conditionType, setConditionType] = useState<'email_not_opened' | 'email_not_clicked' | 'email_not_replied' | 'email_not_delivered'>('email_not_opened');
  const [delayDays, setDelayDays] = useState(3);
  const [excludeDeleted, setExcludeDeleted] = useState(true);
  const [excludeSpam, setExcludeSpam] = useState(true);
  const [excludeBounced, setExcludeBounced] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [maxExecutions, setMaxExecutions] = useState(1);
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);

  const isEditor = user?.role === 'editor';

  // Redirect editors from /crm/automations to /mailforge/automations
  useEffect(() => {
    if (isEditor && location.pathname.includes('/crm/automations')) {
      const newPath = location.pathname.replace('/crm/automations', '/mailforge/automations');
      navigate(newPath, { replace: true });
    }
  }, [isEditor, location.pathname, navigate]);

  // Fetch automations
  const fetchAutomations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/crm/automations');
      setAutomations(response.data.automations || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch automations');
    } finally {
      setLoading(false);
    }
  };

  // Fetch templates (only abandoned type for now)
  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/crm/templates');
      const allTemplates = response.data.templates || [];
      // Filter for abandoned templates only
      setTemplates(allTemplates.filter((t: Template) => t.templateType === 'abandoned'));
    } catch (err: any) {
      console.error('Failed to fetch templates:', err);
    }
  };

  useEffect(() => {
    fetchAutomations();
    fetchTemplates();
  }, []);

  // Reset form
  const resetForm = () => {
    setName('');
    setDescription('');
    setEnabled(true);
    setConditionType('email_not_opened');
    setDelayDays(3);
    setExcludeDeleted(true);
    setExcludeSpam(true);
    setExcludeBounced(true);
    setTemplateId('');
    setMaxExecutions(1);
    setEditingAutomation(null);
  };

  // Open create dialog
  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (automation: Automation) => {
    setEditingAutomation(automation);
    setName(automation.name);
    setDescription(automation.description || '');
    setEnabled(automation.enabled);
    setConditionType(automation.conditions.type);
    setDelayDays(automation.conditions.delayDays);
    setExcludeDeleted(automation.conditions.filters.excludeDeleted !== false);
    setExcludeSpam(automation.conditions.filters.excludeSpam !== false);
    setExcludeBounced(automation.conditions.filters.excludeBounced !== false);
    setTemplateId(automation.action.templateId);
    setMaxExecutions(automation.action.maxExecutions);
    setDialogOpen(true);
  };

  // Handle save
  const handleSave = async () => {
    if (!name || !templateId || !delayDays) {
      setError('Name, template, and delay days are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const automationData = {
        name,
        description,
        enabled,
        conditions: {
          type: conditionType,
          delayDays: parseInt(String(delayDays)),
          filters: {
            emailSent: true,
            excludeDeleted,
            excludeSpam,
            excludeBounced
          }
        },
        action: {
          templateId,
          maxExecutions: parseInt(String(maxExecutions))
        }
      };

      if (editingAutomation) {
        // Update existing
        const response = await api.put(`/api/crm/automations/${editingAutomation._id}`, automationData);
        if (response.data.success) {
          setSuccess('Automation updated successfully!');
          setDialogOpen(false);
          resetForm();
          fetchAutomations();
        }
      } else {
        // Create new
        const response = await api.post('/api/crm/automations', automationData);
        if (response.data.success) {
          setSuccess('Automation created successfully!');
          setDialogOpen(false);
          resetForm();
          fetchAutomations();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save automation');
    } finally {
      setSaving(false);
    }
  };

  // Handle toggle
  const handleToggle = async (automationId: string, currentEnabled: boolean) => {
    setToggling(automationId);
    try {
      const response = await api.put(`/api/crm/automations/${automationId}/toggle`, {
        enabled: !currentEnabled
      });
      if (response.data.success) {
        setAutomations(automations.map(a => 
          a._id === automationId ? { ...a, enabled: !currentEnabled } : a
        ));
        setSuccess(`Automation ${!currentEnabled ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle automation');
    } finally {
      setToggling(null);
    }
  };

  // Handle delete
  const handleDelete = async (automationId: string) => {
    if (!window.confirm('Are you sure you want to delete this automation?')) {
      return;
    }

    try {
      const response = await api.delete(`/api/crm/automations/${automationId}`);
      if (response.data.success) {
        setSuccess('Automation deleted successfully!');
        fetchAutomations();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete automation');
    }
  };

  // Get condition description
  const getConditionDescription = (automation: Automation) => {
    const typeLabels = {
      email_not_opened: 'Email not opened',
      email_not_clicked: 'Email not clicked',
      email_not_replied: 'Email not replied',
      email_not_delivered: 'Email not delivered'
    };
    
    return `${typeLabels[automation.conditions.type]} after ${automation.conditions.delayDays} day(s)`;
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isEditor) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Only Editors can manage automations</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Automations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set up automated workflows to send emails based on lead behavior
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : automations.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <SettingsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Automations Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first automation to automatically send emails based on lead behavior
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Create Automation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Statistics</TableCell>
                <TableCell>Last Run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {automations.map((automation) => (
                <TableRow key={automation._id}>
                  <TableCell>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {automation.name}
                    </Typography>
                    {automation.description && (
                      <Typography variant="caption" color="text.secondary">
                        {automation.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getConditionDescription(automation)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {automation.action.template?.name || 'Template not found'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={automation.enabled ? 'Enabled' : 'Disabled'}
                      color={automation.enabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Total Executions">
                        <Chip label={automation.stats.totalExecutions} size="small" variant="outlined" />
                      </Tooltip>
                      <Tooltip title="Successful">
                        <Chip 
                          label={automation.stats.successfulExecutions} 
                          size="small" 
                          color="success"
                          icon={<CheckCircleIcon />}
                        />
                      </Tooltip>
                      {automation.stats.failedExecutions > 0 && (
                        <Tooltip title="Failed">
                          <Chip 
                            label={automation.stats.failedExecutions} 
                            size="small" 
                            color="error"
                            icon={<ErrorIcon />}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(automation.stats.lastExecutedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title={automation.enabled ? 'Disable' : 'Enable'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggle(automation._id, automation.enabled)}
                          disabled={toggling === automation._id}
                        >
                          {toggling === automation._id ? (
                            <CircularProgress size={20} />
                          ) : automation.enabled ? (
                            <PauseIcon />
                          ) : (
                            <PlayIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(automation)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(automation._id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAutomation ? 'Edit Automation' : 'Create Automation'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Automations run hourly. When a lead meets your conditions, the selected template will be sent automatically.
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Automation Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="e.g., Abandoned Email Follow-Up"
            required
          />

          <TextField
            fullWidth
            label="Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 3 }}
            multiline
            rows={2}
            placeholder="Describe what this automation does"
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Trigger Condition
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>When to trigger</InputLabel>
            <Select
              value={conditionType}
              onChange={(e) => setConditionType(e.target.value as any)}
              label="When to trigger"
            >
              <MenuItem value="email_not_opened">Email not opened</MenuItem>
              <MenuItem value="email_not_clicked">Email not clicked</MenuItem>
              <MenuItem value="email_not_replied">Email not replied</MenuItem>
              <MenuItem value="email_not_delivered">Email not delivered</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Delay (days)"
            type="number"
            value={delayDays}
            onChange={(e) => setDelayDays(parseInt(e.target.value) || 3)}
            sx={{ mb: 2 }}
            inputProps={{ min: 1, max: 365 }}
            helperText="How many days to wait before triggering"
            required
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Filters
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={excludeDeleted}
                onChange={(e) => setExcludeDeleted(e.target.checked)}
              />
            }
            label="Exclude deleted emails"
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={excludeSpam}
                onChange={(e) => setExcludeSpam(e.target.checked)}
              />
            }
            label="Exclude spam reports"
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={excludeBounced}
                onChange={(e) => setExcludeBounced(e.target.checked)}
              />
            }
            label="Exclude bounced emails"
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Action
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Email Template</InputLabel>
            <Select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              label="Email Template"
              required
            >
              {templates.length === 0 ? (
                <MenuItem disabled>No abandoned templates found. Create one in Templates first.</MenuItem>
              ) : (
                templates.map((template) => (
                  <MenuItem key={template._id} value={template._id}>
                    {template.name}
                  </MenuItem>
                ))
              )}
            </Select>
            {templates.length === 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                You need to create an "Abandoned" template first in the Templates page.
              </Alert>
            )}
          </FormControl>

          <TextField
            fullWidth
            label="Max Executions"
            type="number"
            value={maxExecutions}
            onChange={(e) => setMaxExecutions(parseInt(e.target.value) || 1)}
            sx={{ mb: 2 }}
            inputProps={{ min: 1, max: 10 }}
            helperText="Maximum number of times this automation can run for the same lead"
          />

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label="Enable automation"
            sx={{ mb: 2 }}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Variables:</strong> You can use variables like {'{{firstName}}'}, {'{{companyName}}'}, etc. in your email template subject and body. These will be automatically replaced with lead data.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !name || !templateId || !delayDays || templates.length === 0}
          >
            {saving ? <CircularProgress size={20} /> : editingAutomation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CRMAutomations;

